import React, { useCallback, useMemo, useState } from 'react';
import { Box, Grid, SearchInput, useDebounce, VirtualTable } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import AccountTypeDropdown from '../AccountTypeDropdown';
import EmployerSelector from '../EmployerSelector';
import EnrollmentStatusDropdown from '../EnrollmentStatusDropdown';
import ExportButton from '../ExportButton';
import {
  useIsEmployerIsPresent,
  useMultipleEmployerSelectAvailable,
  useShowAppliedSettingsSnackbar,
} from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import PlanSelector from '../PlanSelector';
import PlanYearSelector from '../PlanYearSelector';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import ReportTableWrapper from '../ReportTable/ReportTableWrapper';
import ToggleSSNVisible from '../ToggleSSNVisible';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import ParticipantAccountActivityRange from './ParticipantAccountActivityRange';
import { ParticipantAccountActivityReportTableParams } from './ParticipantActivityReport.types';
import {
  useParticipantAccountActivityReportExportCSVQuery,
  useParticipantAccountActivityReportQuery,
} from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: ParticipantAccountActivityReportTableParams;
  onSetFilterValue: FilterSetters<ParticipantAccountActivityReportTableParams>;
}

const ParticipantActivityReportTable: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);
  const debouncedSearch = useDebounce(filtersValues.searchString);
  const {
    isHasNextPage,
    total,
    isLoading,
    headers,
    nodes,
    lastTimeUpdateDate,
    columnWidths,
    handleLoadMore,
    planOptions,
    isEmptyPlanOptions,
    isExportDisabled,
    onChangeHeaderSize,
  } = useParticipantAccountActivityReportQuery({
    ...filtersValues,
    isMultipleEmployer: isMultipleEmployerAvailable,
    searchString: debouncedSearch,
    isPreviewMode,
  });

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }

    if (!isLoading && !total) {
      if (filtersValues.searchString.trim()) {
        return (
          <PlaceholderBase isLarge>
            <PlaceholderBase.Search />
          </PlaceholderBase>
        );
      }
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return (
      <VirtualTable
        testId="report-table"
        height={isPreviewMode ? 'calc(100vh - 545px)' : 'calc(80vh - 160px)'}
        isLoading={isLoading}
        onResizeHeaders={onChangeHeaderSize}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        columnSizes={columnWidths}
        isIndexMode={true}
        headers={headers}
        items={nodes}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersValues.employerId,
    filtersValues.employerIds,
    isLoading,
    total,
    isPreviewMode,
    onChangeHeaderSize,
    handleLoadMore,
    isHasNextPage,
    columnWidths,
    headers,
    nodes,
  ]);

  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useParticipantAccountActivityReportExportCSVQuery({
      ...filtersValues,
      isMultipleEmployer: true,
    });

  const handleShowAppliedFilterSnackbar = useShowAppliedSettingsSnackbar();
  const handleToggleSSN = useCallback(
    (value: boolean) => {
      onSetFilterValue.isSSNShowed(value);
      handleShowAppliedFilterSnackbar();
    },
    [handleShowAppliedFilterSnackbar, onSetFilterValue]
  );

  return (
    <Box direction="column">
      <TopButtonPortalWrapper
        isPreviewMode={isPreviewMode}
        infoNode={
          <ReportLastUpdateDate
            isWideMode
            isLoading={isLoading}
            key={`${filtersValues.employerId}`}
            value={lastTimeUpdateDate}
          />
        }
        buttonNode={
          <Box direction="row">
            <AddScheduledReportButton
              employerId={filtersValues.employerId}
              employerIds={filtersValues.employerIds}
              isMultipleEmployer={isMultipleEmployerAvailable}
              type={ReportTypeKeys.ParticipantActivity}
              tab={ReportTab.Report}
            />
            <Box margin={{ left: 'spacing12' }}>
              <ExportButton
                isLoading={isExportLoading}
                onClick={handleExportCSV}
                disabled={!isEmployerIsPresent || isExportDisabled}
                text="Download Report (CSV)"
              />
            </Box>
          </Box>
        }
      >
        <ReportTableWrapper
          total={total}
          isLoading={isLoading}
          isPreviewMode={isPreviewMode}
          headerChildren={
            <ToggleSSNVisible
              disabled={!isEmployerIsPresent}
              value={filtersValues.isSSNShowed}
              onChangeValue={handleToggleSSN}
            />
          }
          filters={
            <Box direction="row" fill>
              <Grid
                fill
                columns={{
                  count: isPreviewMode ? 3 : 4,
                  size: 'auto',
                }}
                gap="spacing12"
              >
                {!isPreviewMode && (
                  <SearchInput
                    testId="report"
                    placeholder="Search"
                    value={filtersValues.searchString}
                    disabled={!isEmployerIsPresent}
                    onChange={onSetFilterValue.searchString}
                  />
                )}
                {isMultipleEmployerAvailable ? (
                  <MultipleEmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    values={filtersValues.employerIds}
                    onChangeValues={onSetFilterValue.employerIds}
                  />
                ) : (
                  <EmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    value={filtersValues.employerId}
                    onChangeValue={onSetFilterValue.employerId}
                  />
                )}
                <PlanYearSelector
                  disabled={
                    !isEmployerIsPresent ||
                    (!!isMultipleEmployerAvailable && filtersValues.employerIds?.length !== 1)
                  }
                  value={filtersValues.planYearPeriod}
                  onChangeValue={onSetFilterValue.planYearPeriod}
                />
                <Box fill key={`plan-selector ${isLoading}`}>
                  <PlanSelector
                    disabled={
                      !isEmployerIsPresent ||
                      isEmptyPlanOptions ||
                      (!!isMultipleEmployerAvailable && filtersValues.employerIds?.length !== 1)
                    }
                    options={planOptions}
                    values={filtersValues.reportPlanIds}
                    onChangeValues={onSetFilterValue.reportPlanIds}
                  />
                </Box>
                <AccountTypeDropdown
                  disabled={!isEmployerIsPresent}
                  values={filtersValues.accountTypes}
                  onChangeValues={onSetFilterValue.accountTypes}
                />
                <EnrollmentStatusDropdown
                  disabled={!isEmployerIsPresent}
                  values={filtersValues.enrollmentStatuses}
                  onChangeValues={onSetFilterValue.enrollmentStatuses}
                />
                {!isPreviewMode && (
                  <ParticipantAccountActivityRange
                    disabled={!isEmployerIsPresent}
                    asDate={filtersValues.asDate}
                    onSetAsDate={onSetFilterValue.asDate}
                  />
                )}
              </Grid>
            </Box>
          }
        >
          <Box>{childrenNode}</Box>
        </ReportTableWrapper>
      </TopButtonPortalWrapper>
    </Box>
  );
};
export default ParticipantActivityReportTable;
